import {
  getNewLookTokens,
  NEW_LOOK_RADIUS,
  newLookType,
} from "common/NewLook/tokens.ts";
import Drawer from "common/Drawers/Drawer";
import React, { useContext, useEffect } from "react";
import { QuickConnectContext } from "@shared/features/onboarding/QuickConnect.context";
import Title from "common/Typography/Title";
import { AccountFormContext } from "@shared/features/common/AccountForm.context";
import Flex from "common/Boxes/Flex";
import DemoTag from "common/Tags/DemoTag";
import CreateAccountContent from "features/portfolio/CreateAccount/CreateAccountContent";
import { sharedLib } from "@shared/services/shared.service";
import ConnectExchangeGuide from "features/portfolio/CreateAccount/ConnectExchangeGuide";
import useQuery from "utils/hooks/useQuery";
import styled, { useTheme } from "styled-components";
import useMedia from "utils/hooks/useMedia";
import {
  CREATE_ACCOUNT_PATH,
  INTERCOM_TOTAL_SIZE,
  LARGE_MOBILE,
} from "common/constants";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import { ConnectExchangeTabs } from "@shared/features/portfolio/types";
import { AddClientResponse, LanguageNameSpaces } from "@shared/types";
import { useClientsData } from "@shared/utils/hooks/misc";
import useCreateAccountMessage from "features/configuration/accounts/CreateAccount/useCreateAccountMessage";
import useFormattedNotification from "utils/hooks/useFormattedNotification";
import QuickConnectProvider from "features/configuration/accounts/CreateAccount/QuickConnectProvider";
import { useBottomNavigator } from "utils/hooks/misc";
import { matchPath, useLocation } from "react-router-dom";
import { BASE_QUICK_CONNECT_PATH } from "@shared/common/constants.ts";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation.ts";

const CreateAccountDrawer = () => {
  const location = useLocation();
  const { createAccountDrawerOpen, closeCreateAccountDrawer } =
    useContext(AccountFormContext)!;
  const { getSuccessMessage, contextHolder: successContextHolder } =
    useCreateAccountMessage();
  const { getErrorNotification, contextHolder: errorContextHolder } =
    useFormattedNotification();

  useEffect(() => {
    if (
      !matchPath(location.pathname, { path: CREATE_ACCOUNT_PATH }) &&
      !matchPath(location.pathname, { path: BASE_QUICK_CONNECT_PATH })
    ) {
      closeCreateAccountDrawer();
    }
  }, [location.pathname]);

  return (
    <QuickConnectProvider
      getSuccessMessage={getSuccessMessage}
      getErrorNotification={getErrorNotification}
    >
      <CreateAccountDrawerContent
        open={createAccountDrawerOpen}
        getErrorNotification={getErrorNotification}
        getSuccessMessage={getSuccessMessage}
      />
      {successContextHolder}
      {errorContextHolder}
    </QuickConnectProvider>
  );
};

interface CreateAccountDrawerProps {
  open?: boolean;
  getErrorNotification?: (description: string, title?: string) => void;
  getSuccessMessage?: (
    response?: AddClientResponse,
    exchangesList?: string[]
  ) => void;
}

const CreateAccountDrawerContent = ({
  open,
  getErrorNotification,
  getSuccessMessage,
}: CreateAccountDrawerProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.COMMON);
  const isLargeMobile = useMedia(
    [`(max-width: ${LARGE_MOBILE})`],
    [true],
    false
  );
  const { closeDrawer, quickConnectLoading } = useContext(QuickConnectContext)!;
  const { isDemoWorkspace, getAccountSubExchange, tab } =
    useContext(AccountFormContext)!;
  const [getPortfolioData] =
    sharedLib.sharedApi.portfolio.useLazyGetPortfolioDataQuery();
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const query = useQuery();
  const queryExchange = query.get("exchange") || undefined;
  const accountId = query.get("account") || undefined;
  const { getAccountById } = useClientsData();
  const account = getAccountById(Number(accountId));
  const accountSubExchange = getAccountSubExchange(account);
  const primaryExchange = queryExchange ?? accountSubExchange?.nested_exchange;

  const action = account ? t("drawers.edit") : t("drawers.create");
  // New look: left-aligned like the other drawer titles, on the close
  // button's row.
  const { isNewLook } = useTheme();
  const isSelectStep = !primaryExchange && !account;
  const title = (
    <Flex
      style={{
        marginBottom:
          isNewLook && isSelectStep
            ? 4
            : isBottomNavigator
            ? 16
            : isNewLook
            ? 24
            : 32,
        justifyContent: isNewLook ? "flex-start" : "center",
        alignItems: isNewLook ? "center" : undefined,
        padding: isNewLook && isBottomNavigator ? "0 16px" : undefined,
      }}
    >
      <Title level={3}>
        {t(
          isNewLook
            ? "drawers.create_account_title_new_look"
            : "drawers.create_account_title",
          { action }
        )}
      </Title>
      {isDemoWorkspace &&
        (isNewLook ? (
          <QuietTag>{t("drawers.demo_trading_tag")}</QuietTag>
        ) : (
          <DemoTag isSmall={true} style={{ marginLeft: 8 }} />
        ))}
    </Flex>
  );

  const mainContent = (
    <ContentWrapper
      $isLargeMobile={isLargeMobile}
      $isBottomNavigator={isBottomNavigator}
    >
      <LoadingSpinnerOverlay
        isLoading={quickConnectLoading}
        hideContentWhileLoading={true}
        spinnerMarginTop={isBottomNavigator ? undefined : 250}
        wrap={true}
        wrapStyle={
          isBottomNavigator
            ? { height: "100%", display: "flex", flexDirection: "column" }
            : undefined
        }
      >
        {title}
        <CreateAccountContent
          exchange={primaryExchange}
          onSubmitSuccess={() => getPortfolioData({ use_cache: false })}
          getErrorNotification={getErrorNotification}
          account={account}
          getSuccessMessage={getSuccessMessage}
        />
        {!isBottomNavigator && <div style={{ height: INTERCOM_TOTAL_SIZE }} />}
      </LoadingSpinnerOverlay>
    </ContentWrapper>
  );

  const showExtension = !isNewLook && !isLargeMobile && !!primaryExchange;
  const extension = showExtension && (
    <ExtensionWrapper>
      <ConnectExchangeGuide
        primaryExchange={primaryExchange}
        selectedSubExchange={accountSubExchange}
        isQuickConnect={tab === ConnectExchangeTabs.QUICK_CONNECT}
      />
    </ExtensionWrapper>
  );

  return (
    <StyledDrawer
      open={open}
      onClose={closeDrawer}
      styles={{
        body: {
          display: "flex",
          height: "100%",
          overflow: "unset",
        },
      }}
      size={showExtension ? CONTENT_WIDTH + EXTENSION_WIDTH : CONTENT_WIDTH}
    >
      {extension}
      {mainContent}
    </StyledDrawer>
  );
};

export default React.memo(CreateAccountDrawer);

const QuietTag = styled.span`
  margin-left: 12px;
  padding: 2px 8px;
  border-radius: ${NEW_LOOK_RADIUS.chip}px;
  background: ${(p) => getNewLookTokens(p.theme).well};
  box-shadow: inset 0 0 0 1px ${(p) => getNewLookTokens(p.theme).hairline};
  color: ${(p) => getNewLookTokens(p.theme).secondary};
  ${newLookType.label};
  white-space: nowrap;
`;
const EXTENSION_WIDTH = 450;
const CONTENT_WIDTH = 660;

const StyledDrawer = styled(Drawer)`
  .ant-drawer-body {
    padding: 0 !important;
  }
`;
const ContentWrapper = styled.div<{
  $isBottomNavigator?: boolean;
  $isLargeMobile?: boolean;
}>`
  max-width: ${CONTENT_WIDTH}px;
  padding: ${(p) => {
    if (p.$isBottomNavigator) return "12px 0";
    return p.$isLargeMobile ? "12px 16px 68px" : "12px 24px 68px";
  }};
  height: 100%;
  width: 100%;
  margin-bottom: 60px;
  overflow: ${(p) => (p.$isBottomNavigator ? "hidden" : "auto")};
`;
const ExtensionWrapper = styled.div`
  max-width: ${EXTENSION_WIDTH}px;
  height: 100%;
  padding: 40px 24px 24px;
  background: ${(p) => p.theme.colors.statisticsBgColor};
  overflow-y: unset;
`;
